import {
  AddColumnIcon,
  Checkbox,
  GripVerticalIcon,
  IconButton,
  Popover,
  Text,
} from '@buffer-mono/popcorn'

import { Sortable } from '~publish/components/Sortable'

import type { MetricColumn } from '../../hooks'

import sectionButtonStyles from '../sectionActionButton.module.css'
import styles from './ColumnPickerPopover.module.css'

import type { JSX } from 'react'

interface ColumnPickerPopoverProps {
  availableMetrics: MetricColumn[]
  selectedMetrics: ReadonlySet<string>
  onToggle: (type: string) => void
  onReorder: (activeType: string, overType: string) => void
}

export function ColumnPickerPopover({
  availableMetrics,
  selectedMetrics,
  onToggle,
  onReorder,
}: ColumnPickerPopoverProps): JSX.Element {
  const itemsById = new Map(availableMetrics.map((m) => [m.type, m]))

  return (
    <Popover>
      <Popover.Trigger>
        <IconButton
          className={sectionButtonStyles.neutralBackground}
          label="Choose columns"
          tooltip="Choose columns"
          size="small"
        >
          <AddColumnIcon />
        </IconButton>
      </Popover.Trigger>
      <Popover.Content align="end" side="bottom" className={styles.content}>
        <Text
          as="div"
          size="sm"
          color="subtle"
          weight="medium"
          className={styles.label}
        >
          Columns
        </Text>
        <Sortable
          onReorder={(activeId, overId): void =>
            onReorder(String(activeId), String(overId))
          }
        >
          <Sortable.List
            items={availableMetrics.map((m) => m.type)}
            orientation="vertical"
            className={styles.list}
          >
            {availableMetrics.map(({ type, displayName }) => (
              <Sortable.Item
                key={type}
                id={type}
                className={styles.row}
                data-testid={`column-picker-row-${type}`}
              >
                <Sortable.DragHandle
                  label={`Reorder ${displayName}`}
                  className={styles.handle}
                >
                  <GripVerticalIcon size="xsmall" />
                </Sortable.DragHandle>
                <Checkbox
                  checked={selectedMetrics.has(type)}
                  onChange={(): void => onToggle(type)}
                >
                  {displayName}
                </Checkbox>
              </Sortable.Item>
            ))}
          </Sortable.List>
          <Sortable.Overlay>
            {(activeId): JSX.Element | null => {
              const metric = itemsById.get(String(activeId))
              if (!metric) return null
              // Render a plain presentational row (no useSortable) — the
              // overlay is a visual ghost that follows the pointer/keyboard
              // while the original item stays in place with a dropzone.
              return (
                <div className={`${styles.row} ${styles.overlayRow}`}>
                  <span
                    className={`${styles.handle} ${styles.overlayHandle}`}
                    aria-hidden
                  >
                    <GripVerticalIcon size="xsmall" />
                  </span>
                  <Checkbox checked={selectedMetrics.has(metric.type)} disabled>
                    {metric.displayName}
                  </Checkbox>
                </div>
              )
            }}
          </Sortable.Overlay>
        </Sortable>
      </Popover.Content>
    </Popover>
  )
}
